import { ReactNode, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { useClickOutside } from "@/Common/CustomHooks/useClickOutside";
import { useMount } from "@/Common/CustomHooks/useMount";

import styles from "./AnchoredPopup.module.scss";

interface IAnchoredPopupProps {
    /**
     * Callback that will close the popup
     */
    onClose: () => void;

    /**
     * Position where we want to anchor the popups top corner
     */
    position: { top: number, left: number }

    /**
     * Which side to anchor the top corner from.
     * @default "right"
     */
    sideToAnchor?: "left" | "right";

    /**
     * Component to include in the container
     */
    children: ReactNode;

    /**
     * The unique HTML ID for this specific popup. Defaults to an auto-generate ID unique within this session.
     */
    popupId?: string;

    /**
     * Optional data tag used for testing
     */
    dataCy?: string;
}

interface IAnchoredPopupFooterProps {
    children: ReactNode;
}

interface IAnchoredPopupBodyProps {
    children: ReactNode;
}

interface IAnchoredPopupHeaderProps {
    title: string;
}

function Header(props: Readonly<IAnchoredPopupHeaderProps>): JSX.Element {
    return (
        <header className={styles.header}>
            <h4>{props.title}</h4>
        </header>
    );
}

function Body(props: Readonly<IAnchoredPopupBodyProps>): JSX.Element {
    return (
        <div className={styles.body}>
            {props.children}
        </div>
    );
}

function Footer(props: Readonly<IAnchoredPopupFooterProps>): JSX.Element {
    return (
        <footer className={styles.footer}>
            {props.children}
        </footer>
    );
}

function AnchoredPopup(props: Readonly<IAnchoredPopupProps>): JSX.Element {
    const [position, setPosition] = useState({ top: -9999, left: -9999 }); // Initial offscreen position

    const componentRef = useRef(null);

    useClickOutside(componentRef, () => props.onClose());

    useMount(() => {
        const { height, width } = componentRef.current.getBoundingClientRect();
        const { top, left } = props.position;
        const { innerHeight, innerWidth } = window;

        // Calculate new top position to keep the component within vertical bounds
        const newTop = (top + height <= innerHeight)
            ? top
            : top - (top + height - innerHeight);

        // Calculate new left position based on the side to anchor and keep it within horizontal bounds
        const newLeft = (props.sideToAnchor === "left")
            ? Math.min(left, innerWidth - width)
            : Math.max(left - width, 0);

        // Update position to bring the component onscreen
        setPosition({ top: newTop, left: newLeft });
    });

    return ReactDOM.createPortal(
        <div
            className={styles.anchoredPopupContainer}
            ref={componentRef}
            style={{
                top: position.top,
                left: position.left
            }}
            id={props.popupId}
            data-cy={props.dataCy}
        >
            <div className={styles.subContainer}>
                {props.children}
            </div>
        </div>,
        document.body
    );
}

AnchoredPopup.Header = Header;
AnchoredPopup.Body = Body;
AnchoredPopup.Footer = Footer;

export { AnchoredPopup };
